import React, {useCallback, useEffect} from 'react';
import {useParams, useHistory} from 'react-router';
import {
  CheckoutProduct,
  GetCheckoutProductsResponse,
  Partner,
  UserInfo,
  usePulsoidClient,
} from '../../PulsoidClient';
import {PartnerVideoSection} from './PartnerVideoSection/PartnerVideoSection';
import {PartnerAboutSection} from './PartnerAboutSection/PartnerAboutSection';
import css from './PartnerPage.module.css';
import {PartnerPowerUpSection} from './PartnerPowerUpSection/PartnerPowerUpSection';
import {LoaderWrapper} from '../../system/Loader/Loader';
import {redirectToAuth} from '../../utils/locationUtils';
import {PageFooter} from '../../system/PageFooter/PageFooter';
import {PaddleEvent} from '../../constants/paddleConstants';
import {useBiEvents} from '../../hooks/useBiEvents';

export const PRICING_SUCCESS_URL = '/pricing/success';
export const PRICING_LIFETIME_SUCCESS_URL = '/pricing/lifetime-success';

export const PartnerPage = () => {
  const {slug} = useParams<{slug: string}>();
  const history = useHistory();
  const pulsoidClient = usePulsoidClient({publicRpc: true});
  const pulsoidClientPrivate = usePulsoidClient({
    publicRpc: false,
    noAuthRedirect: true,
  });
  const biEvents = useBiEvents();
  // Hack to not send 2 events for the same paddle checkout session
  const checkoutOpenPrevEventId = React.useRef<string | null>(null);

  const [partner, setPartner] = React.useState<Partner>();
  const [userInfo, setUserInfo] = React.useState<UserInfo>();
  const [statistics, setStatistics] = React.useState<any>(null);
  const [isTwitchOnline, setIsTwitchOnline] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [checkoutInfo, setCheckoutInfo] =
    React.useState<GetCheckoutProductsResponse>();

  const isUserLoggedIn = !!userInfo;
  const isUserSubscribed = userInfo?.premium?.isSubscribed;

  const initializePaddle = useCallback(
    ({sandbox, providerVendorId}: GetCheckoutProductsResponse) => {
      if (sandbox) {
        Paddle.Environment.set('sandbox');
      }
      Paddle?.Setup?.({
        vendor: Number(providerVendorId),
        eventCallback: (data) => {
          const createdAt = data?.eventData?.checkout?.created_at;
          if (
            data.event === PaddleEvent.CHECKOUT_CUSTOMER_DETAILS &&
            (!checkoutOpenPrevEventId.current ||
              checkoutOpenPrevEventId.current !== createdAt)
          ) {
            checkoutOpenPrevEventId.current = createdAt;
            biEvents.sendPartnerCheckoutOpen();
          } else if (data.event === PaddleEvent.CHECKOUT_CLOSE) {
            biEvents.sendPartnerCheckoutClose();
          }
        },
      });
    },
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [partnerResponse, checkoutResponse, userInfo] = await Promise.all(
          [
            pulsoidClient.getPartner(slug),
            pulsoidClient.getBroPlanCheckoutProducts(),
            pulsoidClientPrivate.getUserInfo(),
          ]
        );

        setPartner(partnerResponse.partner);
        setStatistics(partnerResponse.statistics);
        setIsTwitchOnline(partnerResponse.twitchOnline);

        setCheckoutInfo(checkoutResponse);
        initializePaddle(checkoutResponse);

        if (userInfo) {
          setUserInfo(userInfo);
        }
      } catch (error) {
        // TODO: Add proper error state
        console.error(error);
      }
      setIsLoading(false);
    };

    fetchData();
  }, [slug]);

  const onCheckoutStart = async (product: CheckoutProduct) => {
    try {
      if (!isUserLoggedIn) {
        redirectToAuth({registration: true});
      } else if (isUserSubscribed) {
        history.push(`/ui/dashboard`);
      } else {
        Paddle.Checkout.open({
          product: product?.provider?.productId,
          email: userInfo?.email,
          override: product?.provider?.override,
          passthrough: userInfo?.premium?.id,
          success:
            product?.title?.toLowerCase?.() === 'lifetime'
              ? PRICING_LIFETIME_SUCCESS_URL
              : PRICING_SUCCESS_URL,
          allowQuantity: false,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onGetStarted = async () => {
    try {
      redirectToAuth({registration: true});
    } catch (e) {}
  };

  return (
    <LoaderWrapper loading={isLoading}>
      <div className={css.root}>
        <section className={css.partnerSection}>
          <PartnerVideoSection
            partner={partner}
            checkoutInfo={checkoutInfo}
            isTwitchOnline={isTwitchOnline}
            onCheckoutStart={onCheckoutStart}
            onGetStarted={onGetStarted}
            isUserLoggedIn={isUserLoggedIn}
            isUserSubscribed={isUserSubscribed}
          />
        </section>
        <section className={css.partnerSection}>
          <PartnerAboutSection
            partner={partner}
            statistics={statistics}
            isUserLoggedIn={isUserLoggedIn}
          />
        </section>
        <section className={css.partnerSection}>
          <PartnerPowerUpSection
            checkoutInfo={checkoutInfo}
            onCheckoutStart={onCheckoutStart}
            onGetStarted={onGetStarted}
            isUserLoggedIn={isUserLoggedIn}
            isUserSubscribed={isUserSubscribed}
          />
        </section>

        <PageFooter />
      </div>
    </LoaderWrapper>
  );
};
